import React from "react";
import classNames from "classnames";

import { InternalLink } from "ui/link/internal-link";
import type { Product } from "resources/types";
import { deeplinkTranslator } from "modules/App/deeplinkTranslator";

import { useInView } from "react-intersection-observer";
import css from "./teaser.module.scss";
import { useTeaserA11y } from "./useTeaserA11y";

function Container({
  children,
  className,
  colorless,
  onMouseEnter,
}: {
  children: React.ReactNode;
  className?: string;
  colorless?: boolean;
  onMouseEnter?: () => void;
}) {
  const handleMouseEnter = () => {
    const isMobile = "ontouchstart" in document.documentElement;
    if (isMobile || !onMouseEnter) return;
    onMouseEnter();
  };

  return (
    <li
      className={classNames("adtk-item", css.teaser, { [css.teaser__colorless]: colorless }, className)}
      onMouseEnter={handleMouseEnter}
    >
      {children}
    </li>
  );
}

interface LinkProps {
  children: React.ReactNode;
  onClick?: () => void;
  product: Product;
  index?: number;
  strict?: boolean;
}

function Link({ children, onClick, product, index = 0, strict = true }: LinkProps) {
  const linkProps = deeplinkTranslator(product.deeplink, product.slug);
  const strictParams: Record<string, string> = strict ? {} : { "not-strict": "true" };
  const a11y = useTeaserA11y(product);

  const handleItemKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      const nextItem = event.currentTarget.parentElement?.nextElementSibling as HTMLLIElement;

      if (nextItem) {
        const nextLink = nextItem.querySelector("a");
        nextLink?.focus();
      }
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      const prevItem = event.currentTarget.parentElement?.previousElementSibling as HTMLLIElement;

      if (prevItem) {
        const prevLink = prevItem.querySelector("a");
        prevLink?.focus();
      }
    }
  };

  return (
    <InternalLink
      className={css.link}
      aria-label={a11y}
      onClick={onClick}
      route={linkProps.route!}
      tabIndex={index !== 0 ? -1 : 0}
      data-index={index + 1}
      onKeyDown={handleItemKeyDown}
      params={{ ...linkProps.params, ...strictParams }}
    >
      {children}
    </InternalLink>
  );
}

function Cover({ alt, url }: { alt: string; url: string }) {
  const { inView, ref } = useInView({ threshold: 0, triggerOnce: true });

  return (
    <div className={css.cover} ref={ref}>
      {inView && <img aria-hidden="true" className={css.coverImage} src={`${url}?w=360&auto=format`} alt={alt} />}
      <noscript>
        <img aria-hidden="true" className={css.coverImage} src={`${url}?w=360&auto=format`} alt={alt} />
      </noscript>
    </div>
  );
}

function Title({ title }: { title: string }) {
  return (
    <p className={css.title} aria-hidden="true">
      {title}
    </p>
  );
}

function Author({ author }: { author: string | null }) {
  if (!author) {
    return null;
  }

  return (
    <p className={css.author} aria-hidden="true">
      {author}
    </p>
  );
}

interface ActionButtonProps {
  onClick: () => void;
  children: React.ReactNode;
}

function ActionButton({ onClick, children }: ActionButtonProps) {
  return (
    <button className={css.actionBtn} onClick={onClick} type="button" aria-hidden="true" tabIndex={-1}>
      {children}
    </button>
  );
}

export const Teaser = {
  Container,
  ActionButton,
  Author,
  Cover,
  Link,
  Title,
};
